import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {} from '@angular/common';
import { FormlyModule } from '@ngx-formly/core';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { distinctUntilChanged, isObservable, takeUntil } from 'rxjs';
import { DynamicOptionsService } from './services/dynamic-options.service';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FormlyErrorComponent } from '../formly-error.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { commitUserValue } from './user-edit.util';

@Component({
  selector: 'app-custom-radio-group',
  template: `
    <div class="mb-4" [class.hidden]="isFieldHidden()">
      @if (props.label) {
        <label class="font-medium flex justify-between mb-2">
          <span>{{ props.label }}@if (field.props.required) {<span class="dd-required-star"> *</span>}</span>
          <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          @if (isLoading()) {
            <mat-spinner diameter="24"> </mat-spinner>
          }
        </label>
      }
      @if (displayOptions().length > 0) {
        <mat-radio-group
          class="dd-radio-group"
          [class.dd-radio-group--horizontal]="isHorizontal()"
          [class.dd-radio-group--bare]="!hasDescriptions()"
          [required]="props.required"
          [disabled]="props.disabled"
          [formlyDataCy]="field"
          [ngModel]="selectedValue()"
          (ngModelChange)="onSelect($event)">
          @for (option of displayOptions(); track option.value) {
            <div
              class="dd-radio-card"
              [class.dd-radio-card--bare]="!hasDescriptions()"
              [class.dd-radio-card--selected]="selectedValue() === option.value"
              [class.dd-radio-card--disabled]="props.disabled"
              (click)="onSelect(option.value)">
              <!-- aria-label as an INPUT, not [attr.aria-label]: MatRadioButton host-binds
                   aria-label from its own input and would overwrite an attribute binding with
                   null. The card renders the option text in a sibling span, so Material's own
                   <label> stays empty and the native radio has no accessible name without this. -->
              <mat-radio-button
                class="dd-radio-btn"
                [value]="option.value"
                [aria-label]="option.label"></mat-radio-button>
              <span class="dd-radio-card__content">
                <span class="dd-radio-card__head">
                  <span class="dd-radio-card__title">{{ option.label }}</span>
                  @if (option.recommended) {
                    <span class="dd-radio-card__badge">Aanbevolen</span>
                  }
                </span>
                @if (option.description) {
                  <span class="dd-radio-card__desc">{{ option.description }}</span>
                }
              </span>
            </div>
          }
        </mat-radio-group>
      } @else if (isLoading()) {
        <p class="mb-4 text-accent"><i>Laden van options..</i></p>
      } @else {
        <p class="mb-4 text-accent"><i>Geen options beschikbaar</i></p>
      }
      @if (field.props.description) {
        <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
      }
      <app-formly-error [field]="field"></app-formly-error>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .dd-radio-group {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .dd-radio-group--horizontal {
      flex-direction: row;
      flex-wrap: wrap;
    }

    /* Plain list: no card chrome, so a tighter rhythm reads as a simple radio list. */
    .dd-radio-group--bare {
      gap: 0.25rem;
    }

    .dd-radio-group--bare.dd-radio-group--horizontal {
      gap: 1rem;
    }

    .dd-radio-card {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      width: 100%;
      padding: 0.75rem;
      border: 1px solid var(--dd-border-secondary);
      border-radius: 0.5rem;
      background-color: var(--fuse-bg-card);
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        background-color 0.15s ease;
    }

    .dd-radio-group--horizontal .dd-radio-card {
      flex: 1 1 0;
      min-width: 9rem;
    }

    .dd-radio-card:hover {
      background-color: var(--fuse-bg-hover);
    }

    /* Figma: selected card keeps the white surface; only the border turns accent. */
    .dd-radio-card--selected {
      border-color: var(--dd-badge-accent-border);
    }

    /* The state layer is hidden below (Figma has none), so the card itself carries keyboard focus. */
    .dd-radio-card:has(.mdc-radio__native-control:focus-visible) {
      outline: 2px solid var(--dd-focus-ring);
      outline-offset: 2px;
    }

    .dd-radio-card--disabled {
      cursor: default;
      opacity: 0.6;
    }

    /* Bare card = no card: strip border/background/hover so it reads as a plain radio row. */
    .dd-radio-card--bare,
    .dd-radio-card--bare.dd-radio-card--selected {
      border: none;
      padding: 0.125rem 0;
      background-color: transparent;
      /* Single line, no description: centre the dot on the label instead of top-aligning. */
      align-items: center;
    }

    /* Cancel the top-nudge that aligns the dot with a card title; not needed when centred. */
    .dd-radio-card--bare .dd-radio-btn {
      margin-top: 0;
    }

    .dd-radio-card--bare:hover,
    .dd-radio-card--bare.dd-radio-card--selected:hover {
      background-color: transparent;
    }

    /* Its own width, not the card's 100%: with that every option took a whole row. */
    .dd-radio-group--horizontal .dd-radio-card--bare {
      flex: 0 0 auto;
      width: auto;
      min-width: 0;
    }

    /* Bare radio control: hide its empty label and tighten so the circle aligns with the title. */
    .dd-radio-btn {
      margin-top: 0.125rem;
    }

    ::ng-deep .dd-radio-btn .mdc-form-field > label {
      display: none;
    }

    /* Figma card radio: 16px control. A deliberate design-system-wide change (every card radio, not
       only plain sections): the old 20px primary-tinted control had no Figma counterpart. Unselected = card surface + 1px secondary border; selected =
       solid accent disc with an 8px white centre dot. The native control stays for keyboard/a11y;
       only the MDC visuals are replaced. Scoped to .dd-radio-btn, so plain radios elsewhere are untouched. */
    ::ng-deep .dd-radio-btn .mdc-radio {
      padding: 0;
      width: 16px !important;
      height: 16px !important;
      --mdc-radio-state-layer-size: 16px;
    }

    ::ng-deep .dd-radio-btn .mdc-radio__background {
      width: 16px !important;
      height: 16px !important;
      top: 0;
      left: 0;
    }

    ::ng-deep .dd-radio-btn .mdc-radio__background::before {
      display: none;
    }

    ::ng-deep .dd-radio-btn .mdc-radio__outer-circle {
      width: 16px !important;
      height: 16px !important;
      box-sizing: border-box;
      border-width: 1px !important;
      border-color: var(--dd-border-secondary) !important;
      /* Figma Radio fills with white in light AND dark (D-repeat: a white disc on the dark card), so
         one value, not the card surface, which turned the control into a black hole in dark. */
      background-color: #fff;
    }

    ::ng-deep .dd-radio-btn .mdc-radio__inner-circle {
      /* 8px white dot centred in the 16px disc (MDC scales the inner circle from a 20px box). */
      top: 4px;
      left: 4px;
      width: 8px;
      height: 8px;
      box-sizing: border-box;
      border-width: 0 !important;
      /* White in both schemes (Figma), like the unselected fill above. */
      background-color: #fff !important;
    }

    ::ng-deep .dd-radio-btn .mdc-radio__native-control:checked ~ .mdc-radio__background .mdc-radio__outer-circle {
      border-color: var(--dd-badge-accent-border) !important;
      background-color: var(--dd-badge-accent-border);
    }

    .dd-radio-card__content {
      display: flex;
      flex: 1 1 auto;
      min-width: 0;
      flex-direction: column;
      gap: 0.375rem;
    }

    .dd-radio-card__head {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    /* Figma radio option: title = foreground/default/primary, description =
       foreground/default/secondary regular (#545559 light, #c7c7cc dark). */
    .dd-radio-card__title {
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.4;
      color: var(--fuse-text-default);
    }

    .dd-radio-card__desc {
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.5;
      color: var(--dd-accent);
    }

    .dd-radio-card__badge {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      border-radius: 0.375rem;
      font-size: 0.75rem;
      line-height: 1rem;
      font-weight: 500;
      white-space: nowrap;
      color: var(--fuse-primary);
      background-color: rgba(var(--fuse-primary-rgb), 0.12);
    }
  `,
  imports: [
    FormlyModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormlyModule,
    MatRadioGroup,
    MatRadioButton,
    FormsModule,
    InfoTooltipComponent,
    FormlyErrorComponent,
    MatProgressSpinner,
    FormlyDataCyDirective,
  ],
})
export class CustomRadioButtonsComponent extends FieldType implements OnInit, OnDestroy {
  private destroy$ = injectDestroy();
  private dynamicOptionsService = inject(DynamicOptionsService);
  private propsOptions = computed(() => this.props.options);

  // Signals for reactive state management
  availableOptions = signal<Array<{ label: string; value: any; description?: string; recommended?: boolean }>>([]);
  linkedData = signal([]);
  private dynamicFilterValue = signal(undefined);

  // Currently selected option value (normalized id/string), used for card styling + radio state.
  selectedValue = signal<string | number>(undefined);

  // Orientation: 'horizontal' places cards side by side; default 'vertical' stacks them.
  isHorizontal = computed(() => this.props.layout === 'horizontal');

  // Card styling (border/background) is only meaningful when options carry descriptions.
  // Without any description we fall back to a plain radio list: no borders, tight spacing.
  hasDescriptions = computed(() => this.displayOptions().some(option => !!option.description));

  // Options shaped for the card template, with the value already normalized.
  displayOptions = computed(() =>
    this.availableOptions().map(option => ({
      label: option.label,
      value: this.getOptionValue(option),
      description: option.description,
      recommended: option.recommended === true,
    }))
  );

  // Computed value to handle field visibility
  isFieldHidden = computed(
    () => this.props.hideIfOptionsEmpty && (this.availableOptions().length === 0 || this.dynamicFilterValue() == null)
  );
  optionsBasedOnOtherField = computed(() => {
    return this.field.props?.optionsArray?.some(optionsItem => {
      return optionsItem.filters?.some(optionsFilter => optionsFilter.basedOn === 'dynamic');
    });
  });
  isLoading = signal(true);
  loaded = signal(false);

  // Cleanup function for dynamic options subscriptions
  private cleanup?: () => void;

  constructor() {
    super();
    effect(() => {
      if (!isObservable(this.props.options) && this.propsOptions() != null) {
        const options = this.propsOptions();
        if (Array.isArray(options)) {
          this.availableOptions.set(options);
        }
      }
    });
  }

  ngOnInit() {
    if (!this.optionsBasedOnOtherField()) {
      this.initializeOptions();
    }
    this.initializeDynamicOptions();
    this.setupValueChanges();
    this.selectedValue.set(this.getSelectedValue(this.formControl.value));
  }

  private initializeOptions(): void {
    this.dynamicOptionsService.initializeOptions(this.field).subscribe(options => {
      this.availableOptions.set(options);
      this.isLoading.set(false);
      this.loaded.set(true);
    });
  }

  ngOnDestroy() {
    this.cleanup?.();
  }

  private initializeDynamicOptions(): void {
    if (!this.optionsBasedOnOtherField()) {
      return;
    }

    const { linkedData, cleanup } = this.dynamicOptionsService.initializeDynamicOptions(
      {
        field: this.field,
        onOptionsUpdate: options => {
          this.availableOptions.set(options);
          this.isLoading.set(false);
        },
        onFilterValueUpdate: value => {
          this.dynamicFilterValue.set(value);
        },
      },
      this.destroy$
    );

    this.linkedData.set(linkedData);
    this.cleanup = cleanup;
  }

  private setupValueChanges(): void {
    this.field.formControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(value => {
      this.selectedValue.set(this.getSelectedValue(value));
      if (this.dynamicFilterValue()) {
        this.dynamicOptionsService
          .fetchOptionsForValue(this.dynamicFilterValue(), this.linkedData(), '', this.field)
          .subscribe(options => {
            this.availableOptions.set(options);
          });
      }
    });
  }

  getSelectedValue(value: any): string | number {
    return value?.id !== undefined ? value.id : value;
  }

  getOptionValue(option: { label: string; value: any }): string | number {
    return option.value?.id !== undefined ? option.value.id : option.value;
  }

  onSelect(selectedValue: string | number) {
    if (this.props.disabled) {
      return;
    }
    this.selectedValue.set(selectedValue);
    this.field.formControl.markAsTouched();
    const selectedOption = this.availableOptions().find(option => this.getOptionValue(option) === selectedValue);

    if (selectedOption) {
      commitUserValue(this.formControl, selectedOption.value);
    }
  }
}
